/* eslint-disable react-refresh/only-export-components */
import { ColumnHelper } from "@tanstack/react-table";
import isEmpty from "lodash/isEmpty";
import { ManageRolesTableDataItem } from "PFApp/activities/manage_roles/manage_roles.types";
import { ProfileInfo } from "PFApp/activities/manage_roles/parts/profile_info";
import { LOCKED_SHORTLIST_STATES } from "PFApp/constants/shortlist_states";
import { RolesFormKey } from "PFApp/types/activity_form/activity_form_schema";
import {
  ManageRolesFormType,
  ManageRolesMultiForm,
  WMSuggestedProfile
} from "PFApp/types/activity_form/manage_roles_form_schema";
import { Button } from "PFComponents/button";
import { Inline, Stack } from "PFComponents/containers";
import { Pill } from "PFComponents/pill";
import { Typography } from "PFComponents/typography";
import { useErrorsGrowl } from "PFCore/hooks";
import { useInvalidateManageViewRoles } from "PFCore/hooks/queries/roles/use_invalidate_manage_view_roles";
import { useShortlistUpdate } from "PFCore/hooks/queries/shortlists/use_shortlist_update";
import { Id } from "PFTypes/id";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import css from "./cell.module.scss";
import { useSuggestedWmSidePanelActions } from "./suggested_wm_side_panel/use_suggested_wm_side_panel_actions";

const SUGGESTED_WM_COLUMN_WIDTH = 150;

const canRemoveSuggestedTag = (profile: WMSuggestedProfile) =>
  !LOCKED_SHORTLIST_STATES.includes(profile.shortlist_state);

type SuggestedWmCellProps = {
  rolesFormKey: RolesFormKey;
  rowIndex: number;
  activityId?: Id;
};

const SuggestedWmCell = ({ rolesFormKey, rowIndex, activityId }: SuggestedWmCellProps) => {
  const { t: commonT } = useTranslation();
  const growlErrors = useErrorsGrowl();

  const { openSuggestedWmSidePanel } = useSuggestedWmSidePanelActions();
  const name = `${rolesFormKey}.${rowIndex}.suggested_wm` as const;
  const profiles = useWatch<ManageRolesMultiForm>({ name }) as WMSuggestedProfile[] | undefined;
  const canShortlist = useWatch<ManageRolesMultiForm>({
    name: `${rolesFormKey}.${rowIndex}.is_shortlistable`
  }) as ManageRolesFormType;

  const { invalidateAll: invalidateManageViewRoles } = useInvalidateManageViewRoles();
  const { mutateAsync: updateShortlist, isPending } = useShortlistUpdate({
    activityId: activityId ?? -1,
    options: { onSuccess: invalidateManageViewRoles, onError: (error) => growlErrors(error) }
  });

  const getDeleteHandler = (profile: WMSuggestedProfile) =>
    canRemoveSuggestedTag(profile)
      ? () => updateShortlist({ id: profile.shortlist_id, data: { suggested: null } })
      : undefined;

  return (
    <Stack className={css.cell}>
      {isEmpty(profiles) ? (
        <Typography variant="bodyRegular">-</Typography>
      ) : (
        (profiles ?? []).map((profile) => (
          <Pill
            key={profile.id}
            small
            onDelete={canShortlist ? getDeleteHandler(profile) : undefined}
            disabled={isPending}
            classes={{ root: css.pill }}
          >
            <ProfileInfo profile={profile} />
          </Pill>
        ))
      )}
      {canShortlist && (
        <Inline className={css.editButton}>
          <Button
            kind="text"
            text={commonT("edit")}
            small
            onClick={() => activityId && openSuggestedWmSidePanel({ activityId })}
            disabled={!activityId}
          />
        </Inline>
      )}
    </Stack>
  );
};

export const createSuggestedWmColumn = (
  columnHelper: ColumnHelper<ManageRolesTableDataItem>,
  rolesFormKey: RolesFormKey,
  headerLabel: string
) =>
  columnHelper.accessor((row) => row, {
    id: "suggested_wm",
    header: () => <span>{headerLabel}</span>,
    cell: (info) => (
      <SuggestedWmCell
        rolesFormKey={rolesFormKey}
        rowIndex={info.row.index}
        activityId={info.row.original.activityId}
      />
    ),
    size: SUGGESTED_WM_COLUMN_WIDTH,
    meta: {
      getIsCopyPasteBlocked: () => true
    }
  });
